import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Fuel, Loader2, RefreshCw, Pencil, Trash2, ChevronDown, Archive, Camera, Receipt, AlertTriangle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { fuelApi, type FuelReceiptEvidence } from '@/api/fuel';
import { actionItemsApi } from '@/api/action-items';
import { employeePersonalDataApi } from '@/api/employee-personal-data';
import { useFuel, useFuelArchive, useFuelAssignmentConfirmation, useFuelReceiptEvidence } from './hooks/useFuel';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('ru-BY', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function todayInput(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function monthInput(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function decimalInput(value: number): string {
  return Number.isFinite(value) ? String(value).replace('.', ',') : '';
}

function parsePositiveDecimalInput(value: string): number | null {
  const parsed = Number(value.trim().replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function fileDateInput(file: File): string {
  const date = new Date(file.lastModified);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function ReceiptThumb({ receipt }: { receipt: FuelReceiptEvidence }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    fuelApi.getReceiptPhotoBlobUrl(receipt.id, true)
      .then((nextUrl) => {
        if (!active) {
          URL.revokeObjectURL(nextUrl);
          return;
        }
        objectUrl = nextUrl;
        setUrl(nextUrl);
      })
      .catch(() => {
        if (active) setUrl(null);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [receipt.id]);

  if (!url) {
    return (
      <div className="h-14 w-14 rounded border bg-muted flex items-center justify-center">
        <Receipt className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img
        src={url}
        alt="Чек"
        className="h-14 w-14 rounded border object-cover"
      />
    </a>
  );
}

function FuelActionItemsShortcut() {
  const { data } = useQuery({
    queryKey: ['mobile-fuel-action-items-shortcut'],
    queryFn: () => actionItemsApi.list({
      status: 'open',
      sourceModule: 'fuel',
      limit: 20,
    }),
    staleTime: 60_000,
  });

  const items = data?.items ?? [];
  if (items.length === 0) return null;

  const overdueCount = items.filter((item) => item.status === 'OVERDUE').length;
  return (
    <Card className="border-amber-200 bg-amber-50">
      <CardContent className="flex items-center justify-between gap-3 p-3">
        <div className="flex min-w-0 items-center gap-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-700" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-amber-950">
              Требует внимания: {items.length}
            </div>
            <div className="truncate text-xs text-amber-800">
              {overdueCount > 0 ? `Просрочено: ${overdueCount}` : items[0]?.summary}
            </div>
          </div>
        </div>
        <Button asChild size="sm" variant="outline" className="shrink-0 bg-white">
          <Link to="/more/action-items?sourceModule=fuel">Открыть</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

export function FuelPage() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch, isRefetching, save, isSaving, delete: deleteEntry, isDeleting } = useFuel();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [sumInput, setSumInput] = useState('');
  const [showArchive, setShowArchive] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [editingReceiptId, setEditingReceiptId] = useState<string | null>(null);
  const [receiptDateInput, setReceiptDateInput] = useState(todayInput());
  const [receiptNumberInput, setReceiptNumberInput] = useState('');
  const [receiptAmountInput, setReceiptAmountInput] = useState('');
  const [receiptLitersInput, setReceiptLitersInput] = useState('');
  const [receiptCommentInput, setReceiptCommentInput] = useState('');
  const [receiptPhoto, setReceiptPhoto] = useState<File | null>(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [assignmentCarInput, setAssignmentCarInput] = useState('');
  const [assignmentLicenseInput, setAssignmentLicenseInput] = useState('');
  const [assignmentConsumptionInput, setAssignmentConsumptionInput] = useState('');
  const [assignmentConfirmedLocally, setAssignmentConfirmedLocally] = useState(false);

  const { data: archiveData, isLoading: archiveLoading } = useFuelArchive(showArchive);
  const currentFuelMonth = monthInput();
  const receipts = useFuelReceiptEvidence(currentFuelMonth);
  const assignmentConfirmation = useFuelAssignmentConfirmation(currentFuelMonth);
  const personalDataGate = useQuery({
    queryKey: ['mobile-fuel-personal-data-gate', currentFuelMonth],
    queryFn: () => employeePersonalDataApi.getGate(currentFuelMonth, 'fuel.receipt_create'),
    staleTime: 60_000,
  });
  const currentAssignment = assignmentConfirmation.data?.currentAssignment ?? null;
  const currentConfirmation = assignmentConfirmation.data?.confirmation ?? null;
  const hasCurrentAssignmentConfirmation = assignmentConfirmedLocally || Boolean(currentConfirmation?.isCurrentAssignment);
  const assignmentConsumption = parsePositiveDecimalInput(assignmentConsumptionInput);

  useEffect(() => {
    setAssignmentConfirmedLocally(false);
  }, [currentFuelMonth, currentAssignment?.id]);

  const handleEdit = useCallback((id: string, sum: number) => {
    setEditingId(id);
    setSumInput(sum.toString().replace('.', ','));
    setDialogOpen(true);
  }, []);

  const handleSave = useCallback(() => {
    const sum = parseFloat(sumInput.replace(',', '.'));
    if (isNaN(sum) || sum <= 0) return;

    save(
      { sum, id: editingId ?? undefined },
      {
        onSuccess: () => {
          setDialogOpen(false);
          setSumInput('');
          setEditingId(null);
        },
      }
    );
  }, [sumInput, editingId, save]);

  const handleDelete = useCallback((id: string) => {
    if (window.confirm('Удалить эту заправку?')) {
      deleteEntry(id);
    }
  }, [deleteEntry]);

  const openNewReceiptDialog = useCallback(() => {
    setEditingReceiptId(null);
    setReceiptDateInput(todayInput());
    setReceiptNumberInput('');
    setReceiptAmountInput('');
    setReceiptLitersInput('');
    setReceiptCommentInput('');
    setReceiptPhoto(null);
    setReceiptDialogOpen(true);
  }, []);

  const prepareAssignmentDialog = useCallback(() => {
    const source = currentConfirmation?.isCurrentAssignment ? currentConfirmation : currentAssignment;
    setAssignmentCarInput(source?.car || '');
    setAssignmentLicenseInput(source?.license || '');
    setAssignmentConsumptionInput(source ? decimalInput(source.consumptionPer100Km) : '');
    setAssignmentDialogOpen(true);
  }, [currentAssignment, currentConfirmation]);

  const handleOpenNewReceipt = useCallback(() => {
    if (assignmentConfirmation.isLoading || receipts.isLoading || personalDataGate.isLoading) return;
    const isFirstReceiptOfMonth = (receipts.data?.items.length ?? 0) === 0;
    if (isFirstReceiptOfMonth && personalDataGate.error) {
      toast.error('Не удалось проверить сверку персональных данных');
      return;
    }
    if (isFirstReceiptOfMonth && personalDataGate.data && !personalDataGate.data.allowed) {
      const params = new URLSearchParams({
        period: currentFuelMonth,
        workflowKey: 'fuel.receipt_create',
        returnTo: '/more/fuel',
      });
      navigate(`/more/personal-data?${params.toString()}`);
      return;
    }
    if (!currentAssignment || !hasCurrentAssignmentConfirmation) {
      prepareAssignmentDialog();
      return;
    }
    openNewReceiptDialog();
  }, [
    assignmentConfirmation.isLoading,
    currentAssignment,
    currentFuelMonth,
    hasCurrentAssignmentConfirmation,
    navigate,
    openNewReceiptDialog,
    personalDataGate.data,
    personalDataGate.error,
    personalDataGate.isLoading,
    prepareAssignmentDialog,
    receipts.data?.items.length,
    receipts.isLoading,
  ]);

  const handleEditReceipt = useCallback((receipt: FuelReceiptEvidence) => {
    setEditingReceiptId(receipt.id);
    setReceiptDateInput(receipt.receiptDate);
    setReceiptNumberInput(receipt.receiptNumber || '');
    setReceiptAmountInput(receipt.amount ? decimalInput(receipt.amount) : '');
    setReceiptLitersInput(receipt.liters ? decimalInput(receipt.liters) : '');
    setReceiptCommentInput(receipt.comment || '');
    setReceiptPhoto(null);
    setReceiptDialogOpen(true);
  }, []);

  const handleConfirmAssignment = useCallback(() => {
    if (!currentAssignment || !assignmentConsumption) return;
    assignmentConfirmation.confirm(
      {
        month: currentFuelMonth,
        car: assignmentCarInput.trim(),
        license: assignmentLicenseInput.trim(),
        consumptionPer100Km: assignmentConsumption,
      },
      {
        onSuccess: () => {
          setAssignmentConfirmedLocally(true);
          setAssignmentDialogOpen(false);
          openNewReceiptDialog();
        },
      },
    );
  }, [
    assignmentCarInput,
    assignmentConfirmation,
    assignmentConsumption,
    assignmentLicenseInput,
    currentAssignment,
    currentFuelMonth,
    openNewReceiptDialog,
  ]);

  const handleReceiptPhotoChange = useCallback((file: File | null) => {
    setReceiptPhoto(file);
    if (file?.lastModified) {
      setReceiptDateInput(fileDateInput(file));
    }
  }, []);

  const handleSaveReceipt = useCallback(() => {
    if (!editingReceiptId && !hasCurrentAssignmentConfirmation) {
      setReceiptDialogOpen(false);
      prepareAssignmentDialog();
      return;
    }
    const amount = parsePositiveDecimalInput(receiptAmountInput);
    const liters = parsePositiveDecimalInput(receiptLitersInput);
    if (!amount) return;

    receipts.save(
      {
        dto: {
          id: editingReceiptId ?? undefined,
          receiptDate: receiptDateInput,
          receiptNumber: receiptNumberInput,
          amount,
          liters: liters ?? undefined,
          comment: receiptCommentInput,
        },
        photo: receiptPhoto ?? undefined,
      },
      {
        onSuccess: () => {
          setReceiptDialogOpen(false);
          setEditingReceiptId(null);
          setReceiptAmountInput('');
          setReceiptLitersInput('');
          setReceiptPhoto(null);
        },
      },
    );
  }, [
    editingReceiptId,
    hasCurrentAssignmentConfirmation,
    prepareAssignmentDialog,
    receiptCommentInput,
    receiptDateInput,
    receiptAmountInput,
    receiptLitersInput,
    receiptNumberInput,
    receiptPhoto,
    receipts,
  ]);

  const handleDeleteReceipt = useCallback((id: string) => {
    if (window.confirm('Удалить этот чек?')) {
      receipts.delete(id);
    }
  }, [receipts]);

  const fuelRecords = [
    ...(receipts.data?.items ?? []).map((receipt) => ({
      kind: 'receipt' as const,
      id: receipt.id,
      date: `${receipt.receiptDate}T00:00:00.000Z`,
      amount: receipt.amount,
      liters: receipt.liters,
      receipt,
    })),
    ...(data?.entries ?? []).map((entry) => ({
      kind: 'manual' as const,
      id: entry.id,
      date: entry.createdAt,
      amount: entry.sum,
      liters: null,
      entry,
    })),
  ].sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime());

  return (
    <div className="flex flex-col min-h-full">
      <Header
        title="Учет бензина"
        showBack
        rightContent={
          <Button
            variant="ghost"
            size="icon"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw className={`h-5 w-5 ${isRefetching ? 'animate-spin' : ''}`} />
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <Fuel className="h-12 w-12 text-destructive mx-auto mb-4" />
            <p className="text-destructive font-medium">Ошибка загрузки</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="mt-4"
            >
              Повторить
            </Button>
          </div>
        ) : data ? (
          <>
            <FuelActionItemsShortcut />

            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-3">
              <Collapsible>
                <Card>
                  <CollapsibleTrigger className="w-full text-left">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                        <span>Лимит</span>
                        <ChevronDown className="h-4 w-4" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {data.limitHistory?.length > 0 ? (
                        <>
                          <div className="text-xl font-bold">
                            {formatCurrency(data.limitHistory[0].amount)} р
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {format(parseISO(data.limitHistory[0].month), 'LLLL yyyy', { locale: ru })}
                          </div>
                        </>
                      ) : (
                        <div className="text-xl font-bold text-muted-foreground">—</div>
                      )}
                    </CardContent>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0 border-t">
                      <div className="text-xs text-muted-foreground mb-2 mt-2">История (6 мес.)</div>
                      <div className="space-y-1">
                        {data.limitHistory?.slice(1).map((entry) => (
                          <div key={entry.month} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              {format(parseISO(entry.month), 'LLL yyyy', { locale: ru })}
                            </span>
                            <span>{formatCurrency(entry.amount)} р</span>
                          </div>
                        ))}
                        {(!data.limitHistory || data.limitHistory.length <= 1) && (
                          <div className="text-sm text-muted-foreground">Нет истории</div>
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Остаток
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-xl font-bold ${data.remaining < 0 ? 'text-destructive' : 'text-green-600'}`}>
                    {formatCurrency(data.remaining)} р
                  </div>
                  {data.approxLiters > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      ~{data.approxLiters} литров
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium">Заправки этого месяца</CardTitle>
                <Button size="sm" variant="outline" onClick={handleOpenNewReceipt} disabled={assignmentConfirmation.isLoading || personalDataGate.isLoading || receipts.isLoading}>
                  {assignmentConfirmation.isLoading || personalDataGate.isLoading || receipts.isLoading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4 mr-1" />
                  )}
                  Чек
                </Button>
              </CardHeader>
              <CardContent className="space-y-2">
                {receipts.isLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : fuelRecords.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground text-sm">
                    Нет заправок в этом месяце
                  </div>
                ) : (
                  fuelRecords.map((record) => (
                    <div
                      key={`${record.kind}-${record.id}`}
                      className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0"
                    >
                      {record.kind === 'receipt' ? (
                        <ReceiptThumb receipt={record.receipt} />
                      ) : (
                        <div className="h-14 w-14 rounded border bg-muted flex items-center justify-center">
                          <Fuel className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">
                          {record.amount ? `${formatCurrency(record.amount)} р` : 'Сумма не указана'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {format(parseISO(record.date), record.kind === 'receipt' ? 'd MMM yyyy' : 'd MMM, HH:mm', { locale: ru })}
                          {record.kind === 'receipt' && record.liters ? ` · ${formatCurrency(record.liters)} л` : ''}
                          {record.kind === 'receipt' && record.receipt.receiptNumber ? ` · чек ${record.receipt.receiptNumber}` : ''}
                          {record.kind === 'receipt' && record.receipt.matchedLoadId ? ' · сопоставлен' : ''}
                        </div>
                        {record.kind === 'manual' ? (
                          <div className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(record.date), {
                              addSuffix: true,
                              locale: ru,
                            })}
                          </div>
                        ) : record.receipt.comment ? (
                          <div className="truncate text-xs text-muted-foreground">
                            {record.receipt.comment}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            if (record.kind === 'receipt') {
                              handleEditReceipt(record.receipt);
                            } else {
                              handleEdit(record.entry.id, record.entry.sum);
                            }
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => {
                            if (record.kind === 'receipt') {
                              handleDeleteReceipt(record.receipt.id);
                            } else {
                              handleDelete(record.entry.id);
                            }
                          }}
                          disabled={record.kind === 'receipt' ? receipts.isDeleting : isDeleting}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Archive button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowArchive(!showArchive)}
            >
              <Archive className="h-4 w-4 mr-2" />
              {showArchive ? 'Скрыть архив' : 'Показать архив (6 мес.)'}
            </Button>

            {/* Archive content */}
            {showArchive && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Архив заправок</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {archiveLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : archiveData?.months.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      Нет заправок за последние 6 месяцев
                    </div>
                  ) : (
                    archiveData?.months.map((month) => (
                      <Collapsible key={month.month}>
                        <CollapsibleTrigger className="w-full flex items-center justify-between py-2 px-2 rounded hover:bg-muted/50">
                          <div className="flex items-center gap-2">
                            <ChevronDown className="h-4 w-4" />
                            <span className="font-medium">
                              {format(parseISO(month.month), 'LLLL yyyy', { locale: ru })}
                            </span>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {formatCurrency(month.total)} р
                          </span>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="pl-6 space-y-1 py-2">
                            {month.entries.map((entry) => (
                              <div
                                key={entry.id}
                                className="flex items-center justify-between py-1 text-sm"
                              >
                                <span className="text-muted-foreground">
                                  {format(parseISO(entry.createdAt), 'd MMM, HH:mm', { locale: ru })}
                                </span>
                                <span>{formatCurrency(entry.sum)} р</span>
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ))
                  )}
                </CardContent>
              </Card>
            )}
          </>
        ) : null}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Редактировать заправку' : 'Новая заправка'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              Сумма чека (руб)
            </label>
            <Input
              type="text"
              inputMode="decimal"
              value={sumInput}
              onChange={(e) => setSumInput(e.target.value)}
              placeholder="0,00"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !sumInput}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignmentDialogOpen} onOpenChange={setAssignmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Подтвердите машину и карту</DialogTitle>
            <DialogDescription>
              Перед первым чеком месяца подтвердите данные для путевого листа.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!currentAssignment ? (
              <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  Карта на этот месяц не назначена. Обратитесь к администратору бензина.
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Чип
                  </label>
                  <Input aria-label="Чип" value={currentAssignment.chip || '—'} disabled />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Машина
                  </label>
                  <Input
                    aria-label="Машина"
                    type="text"
                    value={assignmentCarInput}
                    onChange={(event) => setAssignmentCarInput(event.target.value)}
                    placeholder="Марка и номер"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Права
                  </label>
                  <Input
                    aria-label="Права"
                    type="text"
                    value={assignmentLicenseInput}
                    onChange={(event) => setAssignmentLicenseInput(event.target.value)}
                    placeholder="Номер водительского удостоверения"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Расход, л/100 км
                  </label>
                  <Input
                    aria-label="Расход, л/100 км"
                    type="text"
                    inputMode="decimal"
                    value={assignmentConsumptionInput}
                    onChange={(event) => setAssignmentConsumptionInput(event.target.value)}
                    placeholder="7,5"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignmentDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleConfirmAssignment}
              disabled={
                assignmentConfirmation.isConfirming ||
                !currentAssignment ||
                !assignmentCarInput.trim() ||
                !assignmentLicenseInput.trim() ||
                !assignmentConsumption
              }
            >
              {assignmentConfirmation.isConfirming ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Подтвердить и добавить чек
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingReceiptId ? 'Редактировать заправку' : 'Новая заправка'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Фото
              </label>
              <Input
                aria-label="Фото"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(event) => handleReceiptPhotoChange(event.target.files?.[0] ?? null)}
              />
              {receiptPhoto && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {receiptPhoto.name}
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Дата
              </label>
              <Input
                aria-label="Дата"
                type="date"
                value={receiptDateInput}
                onChange={(event) => setReceiptDateInput(event.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Сумма чека (руб)
              </label>
              <Input
                aria-label="Сумма чека"
                type="text"
                inputMode="decimal"
                value={receiptAmountInput}
                onChange={(event) => setReceiptAmountInput(event.target.value)}
                placeholder="0,00"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Литры
              </label>
              <Input
                aria-label="Литры"
                type="text"
                inputMode="decimal"
                value={receiptLitersInput}
                onChange={(event) => setReceiptLitersInput(event.target.value)}
                placeholder="Необязательно"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Номер чека
              </label>
              <Input
                aria-label="Номер чека"
                type="text"
                inputMode="numeric"
                value={receiptNumberInput}
                onChange={(event) => setReceiptNumberInput(event.target.value)}
                placeholder="3-12 цифр"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Комментарий
              </label>
              <Input
                aria-label="Комментарий"
                type="text"
                value={receiptCommentInput}
                onChange={(event) => setReceiptCommentInput(event.target.value)}
                placeholder="Необязательно"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSaveReceipt}
              disabled={receipts.isSaving || !receiptDateInput || !parsePositiveDecimalInput(receiptAmountInput) || (!editingReceiptId && !receiptPhoto)}
            >
              {receipts.isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
